import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, FileDown, Plus, Trash2, Upload } from 'lucide-react';
import { useData } from '../../App';
import { Button, Card, Modal } from '../UI';
import { OrgHrDocument, OrgHrKind, OrgHrOverview } from '../../types';
import { api } from '../../services/api';
import { formatDay, todayISO } from '../../utils/format';
import { downloadFile } from '../billing/Setup';
import { errorText } from '../billing/shared';
import { HrTemplatesModal, StatusChip } from './HrDocs';
import { confirmDialog } from '../Dialogs';

// Раздел «Кадры» организации (22.10.2026): штатное расписание Т-3, график отпусков Т-7, табель Т-13, положения —
// генерация по шаблонам из карточек сотрудников и хранение подписанных сканов.

const HINT: Record<OrgHrKind, string> = {
    STAFFING: 'Должности, количество единиц и оклады — из карточек сотрудников организации на дату.',
    VACATION_SCHEDULE: 'Утверждается не позднее 17 декабря на следующий год. Плановые даты — ниже по каждому сотруднику.',
    TIMESHEET: 'По производственному календарю: явки, выходные; отпуск и больничный — по начислениям. Предпраздничный день — 7 часов.',
    REG_PAY: 'При типовом трудовом договоре микропредприятия (ст. 309.2 ТК РФ) не обязательно — условия оплаты в договоре.',
    REG_PD: 'Политика обработки персональных данных нужна всегда (ст. 18.1 152-ФЗ). Лист ознакомления — действующие сотрудники.',
    REG_RULES: 'При типовом трудовом договоре микропредприятия (ст. 309.2 ТК РФ) не обязательно — режим работы в договоре.',
};
const nextYear = () => new Date().getFullYear() + 1;
const thisMonth = () => todayISO().slice(0, 7);

const CreateForm: React.FC<{ orgId: string; kind: OrgHrKind; title: string; staff: OrgHrOverview['staff']; onClose: () => void; onSaved: () => void }> =
    ({ orgId, kind, title, staff, onClose, onSaved }) => {
    const { showNotification } = useData();
    const [date, setDate] = useState(kind === 'VACATION_SCHEDULE' ? `${new Date().getFullYear()}-12-01` : todayISO());
    const [number, setNumber] = useState('');
    const [data, setData] = useState<Record<string, any>>(kind === 'VACATION_SCHEDULE' ? { year: nextYear(), rows: staff.map(s => ({ counterpartyId: s.id, planned: '', days: 28 })) }
        : kind === 'TIMESHEET' ? { month: thisMonth() } : kind === 'STAFFING' ? { period_from: todayISO() } : {});
    const set = (k: string, v: unknown) => setData(d => ({ ...d, [k]: v }));
    const setRow = (i: number, k: string, v: unknown) => setData(d => ({ ...d, rows: d.rows.map((r: any, j: number) => j === i ? { ...r, [k]: v } : r) }));
    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const doc = await api.createOrgHr(orgId, { kind, date, number: number || undefined, data });
            if (doc.fileName) downloadFile(doc.fileName);
            showNotification(`${doc.title} №${doc.number} сформирован`, 'success');
            onSaved(); onClose();
        } catch (err) { showNotification(errorText(err), 'error'); }
    };
    const input = (label: string, value: string, onChange: (v: string) => void, type = 'text', placeholder?: string) => (
        <label className="block text-xs"><span className="text-slate-600">{label}</span>
            <input className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm" type={type} value={value} placeholder={placeholder}
                   onChange={e => onChange(e.target.value)} /></label>
    );
    return (
        <Modal isOpen onClose={onClose} title={title}>
            <form onSubmit={submit} className="space-y-3 text-sm">
                <p className="text-[11px] text-slate-500">{HINT[kind]}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {input('Дата документа', date, setDate, 'date')}
                    {input('Номер', number, setNumber, 'text', 'авто')}
                    {kind === 'STAFFING' && <>{input('Действует с', data.period_from || '', v => set('period_from', v), 'date')}
                        {input('Утверждено приказом №', data.order_number || '', v => set('order_number', v))}
                        {input('Дата приказа', data.order_date || '', v => set('order_date', v), 'date')}</>}
                    {kind === 'TIMESHEET' && input('Месяц', data.month || '', v => set('month', v), 'month')}
                    {kind === 'VACATION_SCHEDULE' && input('На год', String(data.year || ''), v => set('year', Number(v)), 'number')}
                </div>
                {kind === 'VACATION_SCHEDULE' && (
                    <div className="space-y-1">
                        {staff.length === 0 && <p className="text-xs text-amber-700">В организации нет сотрудников в штате.</p>}
                        {(data.rows as any[]).map((r, i) => (
                            <div key={r.counterpartyId} className="grid grid-cols-[1fr_9rem_4.5rem] gap-2 items-center text-xs">
                                <span className="min-w-0 break-words">{staff.find(s => s.id === r.counterpartyId)?.name}</span>
                                <input type="date" aria-label="Начало отпуска" className="rounded border border-slate-300 px-1.5 py-1" value={r.planned}
                                       onChange={e => setRow(i, 'planned', e.target.value)} />
                                <input type="number" aria-label="Дней" min={1} className="rounded border border-slate-300 px-1.5 py-1" value={r.days}
                                       onChange={e => setRow(i, 'days', Number(e.target.value))} />
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end gap-2 pt-2 border-t">
                    <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button type="submit" size="sm">Сформировать</Button>
                </div>
            </form>
        </Modal>
    );
};

const DocRow: React.FC<{ d: OrgHrDocument; onChanged: () => void }> = ({ d, onChanged }) => {
    const { showNotification } = useData();
    const fileRef = useRef<HTMLInputElement>(null);
    const run = async (fn: () => Promise<unknown>, ok?: string) => {
        try { await fn(); if (ok) showNotification(ok, 'success'); onChanged(); } catch (e) { showNotification(errorText(e), 'error'); }
    };
    const period = d.kind === 'TIMESHEET' && d.data.month ? ` за ${d.data.month.slice(5)}.${d.data.month.slice(0, 4)}`
        : d.kind === 'VACATION_SCHEDULE' && d.data.year ? ` на ${d.data.year} год` : '';
    return (
        <div className="py-1.5 space-y-1 text-xs">
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-slate-500">{formatDay(d.date)}</span>
                <span className="min-w-0 flex-1 break-words">{d.title}{period} №{d.number}</span>
                <StatusChip status={d.status} />
                <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5" title="Сформировать заново по текущим данным и скачать"
                    onClick={() => run(async () => downloadFile((await api.orgHrFile(d.id)).fileName))}><FileDown size={12} /> DOCX</button>
                <button className="text-slate-400 hover:text-red-600" title="Удалить" onClick={async () => (await confirmDialog(`Удалить «${d.title}» №${d.number}?`)) && run(() => api.deleteOrgHr(d.id), 'Удалено')}><Trash2 size={12} /></button>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-[4.5rem] text-[11px] text-slate-600">
                {d.status !== 'SIGNED' && <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!d.sentAt} onChange={e => run(() => api.setOrgHrStatus(d.id, { sent: e.target.checked }))} /> отправлен на подпись</label>}
                {d.scanFile
                    ? <span className="inline-flex items-center gap-2"><button className="text-emerald-700 hover:underline inline-flex items-center gap-0.5" onClick={() => downloadFile(d.scanFile!)}><Check size={12} /> скан подписанного</button>
                        <span className="text-slate-400">{d.scanUploadedBy}</span>
                        <button className="text-slate-400 hover:text-red-600" title="Убрать скан" onClick={async () => (await confirmDialog('Убрать скан?')) && run(() => api.deleteOrgHrScan(d.id))}><Trash2 size={11} /></button></span>
                    : <button className="text-blue-600 hover:underline inline-flex items-center gap-0.5" onClick={() => fileRef.current?.click()}><Upload size={12} /> загрузить подписанный скан</button>}
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.webp" className="hidden"
                       onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => api.uploadOrgHrScan(d.id, f), 'Скан загружен'); }} />
                <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={d.originalAtAccountant} onChange={e => run(() => api.setOrgHrStatus(d.id, { originalAtAccountant: e.target.checked }))} /> оригинал у бухгалтера</label>
            </div>
        </div>
    );
};

export const OrgHr: React.FC<{ orgId: string }> = ({ orgId }) => {
    const [ov, setOv] = useState<OrgHrOverview | null>(null);
    const [creating, setCreating] = useState<OrgHrKind | null>(null);
    const [templates, setTemplates] = useState(false);
    const load = useCallback(() => { api.orgHr(orgId).then(setOv).catch(() => setOv(null)); }, [orgId]);
    useEffect(load, [load]);
    if (!ov) return <p className="text-sm text-slate-400">Загрузка…</p>;
    return (
        <div className="space-y-4">
            <Card title="Кадровые документы организации">
                <div className="space-y-3">
                    <p className="text-xs text-slate-500">Сотрудников в штате: {ov.staff.length}{ov.staff.length ? ` — ${ov.staff.map(s => s.name).join(', ')}` : ''}.
                        Документы формируются из карточек сотрудников (Справочники → Сотрудники); DOCX всегда собирается заново по текущим данным.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {ov.kinds.map(k => (
                            <button key={k.kind} className="text-left rounded border border-slate-200 p-2 hover:border-blue-300 hover:bg-blue-50/40" onClick={() => setCreating(k.kind)}>
                                <span className="flex items-center gap-1 text-sm font-semibold"><Plus size={14} /> {k.title}</span>
                                <span className="block text-[11px] text-slate-500 mt-0.5">{HINT[k.kind]}</span>
                            </button>
                        ))}
                    </div>
                    <div><Button size="sm" variant="outline" onClick={() => setTemplates(true)}>Шаблоны</Button></div>
                </div>
            </Card>
            <Card title={`Сформированные (${ov.documents.length})`}>
                {ov.documents.length === 0 ? <p className="text-xs text-slate-400">Документов пока нет.</p>
                    : <div className="divide-y divide-slate-100">{ov.documents.map(d => <DocRow key={d.id} d={d} onChanged={load} />)}</div>}
            </Card>
            {creating && <CreateForm orgId={orgId} kind={creating} title={ov.kinds.find(k => k.kind === creating)!.title} staff={ov.staff}
                                     onClose={() => setCreating(null)} onSaved={load} />}
            {templates && <HrTemplatesModal onClose={() => setTemplates(false)} />}
        </div>
    );
};
